import type { BadgeProps } from "@pericles/framework-frontend";
import {
  AI_REVIEW_STATUS,
  type AiReviewStatus,
  EXECUTION_STATUS,
  PRISMA_EXECUTION_STATUS,
  type ExecutionStatus,
  parseExecutionStatus,
  WORK_STATUSES,
  type WorkStatus,
} from "@pericles/shared";

export type StatusBadgeVariant = BadgeProps["variant"];

export const WORK_STATUS_UI: Record<WorkStatus, { label: string; variant: StatusBadgeVariant }> = {
  not_started: { label: "Not started", variant: "default" },
  in_progress: { label: "In progress", variant: "warning" },
  blocked: { label: "Blocked", variant: "danger" },
  done: { label: "Done", variant: "success" },
  cancelled: { label: "Cancelled", variant: "danger" },
};

export const EXECUTION_STATUS_UI: Record<ExecutionStatus, { label: string; variant: StatusBadgeVariant }> = {
  [PRISMA_EXECUTION_STATUS.notRequested]: { label: "Not requested", variant: "default" },
  [PRISMA_EXECUTION_STATUS.pending]: { label: "Pending", variant: "warning" },
  [PRISMA_EXECUTION_STATUS.notPlanned]: { label: "Not planned", variant: "default" },
  [PRISMA_EXECUTION_STATUS.planned]: { label: "Planned", variant: "default" },
  [PRISMA_EXECUTION_STATUS.inProgress]: { label: "In progress", variant: "warning" },
  [PRISMA_EXECUTION_STATUS.blocked]: { label: "Blocked", variant: "danger" },
  [PRISMA_EXECUTION_STATUS.completed]: { label: "Completed", variant: "success" },
  [PRISMA_EXECUTION_STATUS.done]: { label: "Done", variant: "success" },
  [PRISMA_EXECUTION_STATUS.failed]: { label: "Failed", variant: "danger" },
  [PRISMA_EXECUTION_STATUS.dropped]: { label: "Dropped", variant: "default" },
};

export const AI_REVIEW_STATUS_UI: Record<AiReviewStatus, { label: string; variant: StatusBadgeVariant }> = {
  [AI_REVIEW_STATUS.none]: { label: "Not applicable", variant: "default" },
  [AI_REVIEW_STATUS.pending]: { label: "Pending", variant: "warning" },
  [AI_REVIEW_STATUS.in_progress]: { label: "In progress", variant: "warning" },
  [AI_REVIEW_STATUS.completed]: { label: "Completed", variant: "success" },
  [AI_REVIEW_STATUS.rejected]: { label: "Rejected", variant: "danger" },
};

const WORK_STATUS_ALIASES: Record<string, WorkStatus> = {
  completed: "done",
  notrequested: "not_started",
  not_requested: "not_started",
};

const AI_REVIEW_ALIASES: Record<string, AiReviewStatus> = {
  approved: AI_REVIEW_STATUS.completed,
  accepted_as_is: AI_REVIEW_STATUS.completed,
  changes_requested: AI_REVIEW_STATUS.in_progress,
  edited: AI_REVIEW_STATUS.in_progress,
  in_review: AI_REVIEW_STATUS.pending,
  not_applicable: AI_REVIEW_STATUS.none,
};

function normalizeString(input: unknown): string | null {
  if (typeof input === "string") return input.trim();
  return null;
}

export function normalizeWorkStatus(input: unknown): WorkStatus | null {
  const normalized = normalizeString(input);
  if (!normalized) return null;

  const canonical = normalized.toLowerCase();
  if (WORK_STATUS_ALIASES[canonical]) {
    return WORK_STATUS_ALIASES[canonical];
  }

  if (WORK_STATUSES.includes(canonical as WorkStatus)) {
    return canonical as WorkStatus;
  }

  return null;
}

export function normalizeExecutionStatus(input: unknown): ExecutionStatus | null {
  return parseExecutionStatus(input);
}

export function normalizeMeetingStatusForApi(input: unknown): ExecutionStatus {
  if (input === undefined || input === null) {
    return PRISMA_EXECUTION_STATUS.planned;
  }

  const normalized = parseExecutionStatus(input ?? undefined);
  if (!normalized) {
    const allowed = Object.values(PRISMA_EXECUTION_STATUS).join(", ");
    throw new Error(`Unsupported meeting status "${String(input)}". Allowed: ${allowed}`);
  }

  return normalized;
}

export function normalizeAiReviewStatus(input: unknown): AiReviewStatus | null {
  const normalized = normalizeString(input);
  if (!normalized) return null;

  const lower = normalized.toLowerCase();
  if (AI_REVIEW_ALIASES[lower]) {
    return AI_REVIEW_ALIASES[lower];
  }

  const values: AiReviewStatus[] = Object.values(AI_REVIEW_STATUS);
  if (values.includes(lower as AiReviewStatus)) {
    return lower as AiReviewStatus;
  }

  return null;
}
